// Query-param contract for opening the inbox in "compose" (ephemeral draft) mode.
//
// "Start a Conversation" in the source directory routes to /my_inbox in compose mode. The inbox
// resolves these params into an ephemeral draft conversation (no DB row yet) so the user types the
// first message in the real inbox composer; the conversation is created on that first send — see
// useComposeDraft + MessagesContext's draft send path.
//
// INVARIANT: compose params are only ever set for a genuinely-new thread. MessageButton checks for
// an existing conversation about the same origin first and, if one exists, deep-links to it with
// ?conversation=<uuid> instead — so the inbox never has to disambiguate here.

export const COMPOSE_ORIGIN_TYPE_PARAM = "compose_origin_type";
export const COMPOSE_ORIGIN_ID_PARAM = "compose_origin_id";
// Profile-to-profile compose (PRs): no promo-entity origin, just the recipient's profile id.
export const COMPOSE_PROFILE_ID_PARAM = "compose_profile_id";
// Optional prefill: a canned message body and a subject override for the draft. Used by "Request
// Sample" so the composer opens with the sample-request template already typed in and a marker
// subject; both stay fully editable once the composer is open.
export const COMPOSE_BODY_PARAM = "compose_body";
export const COMPOSE_SUBJECT_PARAM = "compose_subject";

// V1-COMPAT: when V1 hosts the copied inbox it lives at /my_inbox_v2; navigations must stay on-host.
export function inboxPath() {
  return globalThis.Qwoted?.V1_HOSTED ? "/my_inbox_v2" : "/my_inbox";
}

// Attach the optional body/subject prefill params when a caller supplies them. Kept separate so
// every composeUrl variant (origin or profile) can opt into the same prefill contract.
function appendPrefillParams(params, { body, subject } = {}) {
  if (body) params.set(COMPOSE_BODY_PARAM, body);
  if (subject) params.set(COMPOSE_SUBJECT_PARAM, subject);
}

// Build the inbox URL that opens compose mode for a promo entity (Expert/Product/Company).
export function composeUrl({ originType, originId, body, subject }) {
  const params = new URLSearchParams();
  params.set(COMPOSE_ORIGIN_TYPE_PARAM, originType);
  params.set(COMPOSE_ORIGIN_ID_PARAM, String(originId));
  appendPrefillParams(params, { body, subject });
  return `${inboxPath()}?${params.toString()}`;
}

// Build the inbox URL that opens compose mode for a direct profile recipient (a PR / promoter),
// which has no promo-entity origin.
export function composeUrlForProfile({ profileId, body, subject }) {
  const params = new URLSearchParams();
  params.set(COMPOSE_PROFILE_ID_PARAM, String(profileId));
  appendPrefillParams(params, { body, subject });
  return `${inboxPath()}?${params.toString()}`;
}

// Read compose params from a search string (defaults to the live URL). Returns null unless a
// complete origin pair OR a profile id is present, so a stray param can't half-activate compose
// mode. Exactly one of {origin, profile} is populated per the params in the URL.
export function readComposeParams(search) {
  if (typeof window === "undefined" && search === undefined) return null;
  const params = new URLSearchParams(search ?? window.location.search);
  const originType = params.get(COMPOSE_ORIGIN_TYPE_PARAM);
  const originId = params.get(COMPOSE_ORIGIN_ID_PARAM);
  const profileId = params.get(COMPOSE_PROFILE_ID_PARAM);
  // Prefill is an add-on to a valid compose target; it never activates compose mode on its own.
  const body = params.get(COMPOSE_BODY_PARAM) || null;
  const subject = params.get(COMPOSE_SUBJECT_PARAM) || null;
  if (originType && originId) return { originType, originId, profileId: null, body, subject };
  if (profileId) return { originType: null, originId: null, profileId, body, subject };
  return null;
}

// Read just the pre-fill body param, independent of whether compose mode is active. The same canned
// body (e.g. the Request Sample template) is seeded into the composer whether the button opened a
// brand-new draft (?compose_origin_*) OR deep-linked into an existing thread (?conversation=<uuid>).
export function readComposeBody(search) {
  if (typeof window === "undefined" && search === undefined) return "";
  const params = new URLSearchParams(search ?? window.location.search);
  return params.get(COMPOSE_BODY_PARAM) || "";
}

// Strip the pre-fill params from an existing-thread deep-link once the body has been seeded, so a
// reload doesn't re-seed and the URL stays clean. Compose mode manages its own params via
// stripComposeParamsFromUrl on finishCompose, so this only touches the body/subject prefill.
export function stripComposeBodyFromUrl() {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  if (!params.has(COMPOSE_BODY_PARAM) && !params.has(COMPOSE_SUBJECT_PARAM)) return;
  params.delete(COMPOSE_BODY_PARAM);
  params.delete(COMPOSE_SUBJECT_PARAM);
  const search = params.toString();
  const newUrl = `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`;
  window.history.replaceState({}, "", newUrl);
}

// Remove the compose params from the current URL via replaceState (no history entry / no reload),
// preserving every other param and the hash. Used once the draft becomes a real conversation.
export function stripComposeParamsFromUrl() {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  if (
    !params.has(COMPOSE_ORIGIN_TYPE_PARAM) &&
    !params.has(COMPOSE_ORIGIN_ID_PARAM) &&
    !params.has(COMPOSE_PROFILE_ID_PARAM)
  ) {
    return;
  }
  params.delete(COMPOSE_ORIGIN_TYPE_PARAM);
  params.delete(COMPOSE_ORIGIN_ID_PARAM);
  params.delete(COMPOSE_PROFILE_ID_PARAM);
  params.delete(COMPOSE_BODY_PARAM);
  params.delete(COMPOSE_SUBJECT_PARAM);
  const search = params.toString();
  const newUrl = `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`;
  window.history.replaceState({}, "", newUrl);
}
